/**
 * The panel beside the page: what the page itself cannot show.
 *
 * With nothing picked it is the document — title, dates, discount, default
 * terms and an outline to jump about with. With a block picked it is that
 * block's settings, and for the price it is the whole of the pricing editor,
 * because lines, packages and tax do not belong typed into a letter.
 * Decision 81.
 */
import { cn } from '../../lib/cn.js'
import { Icon } from '../Icon.js'
import { Button, Field, Input, Textarea } from '../ui.js'
import { BLOCK_KINDS } from './BlockLibrary.js'
import { BLOCK_ICON, FilesField, PhotoField } from './CanvasBlock.js'
import { SECTION_LABEL, type DraftSection, type ProposalDraft } from './draft.js'
import { PriceInput, PricingInspector, PROSE_HINT } from './PricingInspector.js'
import type { DraftApply } from './useDraftHistory.js'

export const GHOST_PRICING = '__pricing'

export function BlockInspector({
  draft,
  apply,
  selected,
  onSelect,
  onPatch,
  onRemove,
  mode,
  clientId,
  jobId,
  focusOption,
  aiAvailable,
}: {
  draft: ProposalDraft
  apply: DraftApply
  /** A section key, `GHOST_PRICING`, or null for the document. */
  selected: string | null
  onSelect: (key: string | null) => void
  onPatch: (key: string, next: Partial<DraftSection>, group?: string) => void
  onRemove: (key: string) => void
  mode: 'quote' | 'template'
  clientId: string | null
  jobId: string | null
  focusOption: string | null
  aiAvailable: boolean
}) {
  const section = selected ? draft.sections.find((entry) => entry.key === selected) : undefined

  if (selected === GHOST_PRICING || section?.kind === 'PRICING') {
    return (
      <Panel
        icon="pricing"
        title="Pricing"
        description="Every change shows on the page as you make it."
        onBack={() => onSelect(null)}
      >
        <PricingInspector draft={draft} apply={apply} mode={mode} clientId={clientId} focusOption={focusOption} />
      </Panel>
    )
  }

  if (section) {
    const about = BLOCK_KINDS.find((entry) => entry.kind === section.kind)?.description ?? ''
    return (
      <Panel icon={BLOCK_ICON[section.kind]} title={SECTION_LABEL[section.kind]} description={about} onBack={() => onSelect(null)}>
        {section.kind === 'COVER' ? (
          <div className="flex flex-col gap-1.5">
            <p className="text-xs font-medium text-ink-700">Photo</p>
            <PhotoField
              section={section}
              onPatch={(next) => onPatch(section.key, next)}
              jobId={jobId}
              mode={mode}
              variant="panel"
              active
            />
          </div>
        ) : null}

        {section.kind === 'IMAGE' ? (
          <div className="flex flex-col gap-1.5">
            <p className="text-xs font-medium text-ink-700">Photos and PDFs</p>
            <p className="text-xs text-ink-500">
              Photos print three to a row. A PDF prints by name and the customer opens it from their link.
            </p>
            <FilesField section={section} onPatch={(next) => onPatch(section.key, next)} jobId={jobId} mode={mode} />
          </div>
        ) : null}

        {section.kind === 'TERMS' ? (
          <p className="rounded-lg bg-ink-50 p-3 text-xs leading-5 text-ink-600 ring-1 ring-ink-200">
            Leave this block's words blank and the default terms print here. The default is under the document
            settings.
          </p>
        ) : null}

        <div className="rounded-lg bg-ink-50 p-3 text-xs leading-5 text-ink-600 ring-1 ring-ink-200">
          <p className="font-semibold text-ink-800">Writing on the page</p>
          <p className="mt-1">{PROSE_HINT}</p>
          {aiAvailable ? (
            <p className="mt-1.5 flex items-start gap-1.5">
              <Icon name="sparkles" className="mt-0.5 size-3.5 shrink-0 text-violet-600" />
              Select some words, or none, and press the sparkle above the block for writing help. It knows the job,
              the customer and the rest of the proposal.
            </p>
          ) : null}
        </div>

        <Button
          type="button"
          size="sm"
          variant="ghost"
          icon={<Icon name="trash" className="size-3.5" />}
          onClick={() => onRemove(section.key)}
        >
          Remove this block
        </Button>
      </Panel>
    )
  }

  return (
    <Panel icon="quotes" title="Document" description="The proposal as a whole.">
      <Field label="Proposal title" htmlFor="proposal-title" hint="Also the email's subject, and the heading when there is no cover.">
        <Input
          id="proposal-title"
          value={draft.title}
          placeholder="Termite protection for your home"
          onChange={(event) => apply((d) => ({ ...d, title: event.target.value }), 'doc-title')}
        />
      </Field>

      {mode === 'quote' ? (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Valid until" htmlFor="proposal-valid">
            <Input
              id="proposal-valid"
              type="date"
              value={draft.validUntil ?? ''}
              onChange={(event) => apply((d) => ({ ...d, validUntil: event.target.value || null }))}
            />
          </Field>
          <Field label="Discount ($)" htmlFor="proposal-discount" hint="Before GST.">
            <PriceInput
              id="proposal-discount"
              value={draft.discountAmount}
              onChange={(cents) => apply((d) => ({ ...d, discountAmount: Math.max(0, cents ?? 0) }), 'doc-discount')}
            />
          </Field>
        </div>
      ) : (
        <Field label="Valid for (days)" htmlFor="proposal-days" hint="Counted from the day a proposal is raised from this.">
          <Input
            id="proposal-days"
            inputMode="numeric"
            value={draft.validForDays ?? ''}
            onChange={(event) => {
              const days = Number.parseInt(event.target.value, 10)
              apply((d) => ({ ...d, validForDays: Number.isFinite(days) && days > 0 ? Math.min(days, 365) : null }), 'doc-days')
            }}
          />
        </Field>
      )}

      <Field label="Default terms" htmlFor="proposal-terms" hint="Printed at the end, unless a Terms block has its own words.">
        <Textarea
          id="proposal-terms"
          rows={7}
          value={draft.termsText}
          onChange={(event) => apply((d) => ({ ...d, termsText: event.target.value }), 'doc-terms')}
        />
      </Field>

      <div>
        <p className="text-[10px] font-bold tracking-[0.06em] text-ink-500 uppercase">On the page</p>
        {draft.sections.length === 0 ? (
          <p className="mt-1 text-xs text-ink-500">Nothing yet.</p>
        ) : (
          <ol className="mt-1 flex flex-col gap-0.5">
            {draft.sections.map((entry) => (
              <li key={entry.key}>
                <button
                  type="button"
                  onClick={() => onSelect(entry.key)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] text-ink-800 hover:bg-ink-50"
                >
                  <Icon name={BLOCK_ICON[entry.kind]} className="size-3.5 text-ink-500" />
                  <span className="min-w-0 flex-1 truncate">{entry.title.trim() || SECTION_LABEL[entry.kind]}</span>
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>
    </Panel>
  )
}

function Panel({
  icon,
  title,
  description,
  onBack,
  children,
}: {
  icon: Parameters<typeof Icon>[0]['name']
  title: string
  description: string
  onBack?: () => void
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-2.5">
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-brand-800 text-white">
          <Icon name={icon} className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-ink-900">{title}</h3>
          <p className="text-xs text-ink-500">{description}</p>
        </div>
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className={cn('shrink-0 rounded-md px-2 py-1 text-xs font-medium text-ink-600 hover:bg-ink-100 hover:text-ink-900')}
          >
            Document
          </button>
        ) : null}
      </div>
      {children}
    </div>
  )
}
